import { useEffect, useState } from "react";
import Icon from "./Icon";
import { registerCredentialsDialog } from "../utils/credentials";

// Global host for one-time login credentials. Mounted once at the app root;
// showCredentials() (utils/credentials) opens it. Shows username + temp
// password as read-only fields with per-field copy buttons plus "Copy both",
// and stays open until dismissed — the backend never reveals them again.
export default function CredentialsDialog() {
  const [creds, setCreds] = useState(null); // { username, password } | null

  useEffect(() => {
    registerCredentialsDialog((c) => setCreds(c));
    return () => registerCredentialsDialog(null);
  }, []);

  // Close on Escape while open.
  useEffect(() => {
    if (!creds) return;
    const onKey = (e) => { if (e.key === "Escape") setCreds(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [creds]);

  if (!creds) return null;

  const close = () => setCreds(null);

  return (
    <div style={S.overlay} onClick={close}>
      <div style={S.card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={S.head}>
          <div style={S.iconWrap}><Icon name="key" size={18} /></div>
          <div>
            <div style={S.title}>One-time login credentials</div>
            <div style={S.sub}>Shown only once — copy and share now. First login forces a password change.</div>
          </div>
        </div>

        <CredField label="Username" value={creds.username} />
        <CredField label="Temporary password" value={creds.password} mono />

        <div style={S.footer}>
          <CopyButton
            label="Copy both"
            text={`Username: ${creds.username}\nPassword: ${creds.password}`}
            variant="secondary"
          />
          <button className="btn btn-primary" onClick={close}>Done</button>
        </div>
      </div>
    </div>
  );
}

function CredField({ label, value, mono }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label className="field-label" style={{ display: "block", marginBottom: 4 }}>{label}</label>
      <div style={S.fieldRow}>
        <input
          className="input"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          style={mono ? { fontFamily: "monospace" } : undefined}
        />
        <CopyButton text={value} />
      </div>
    </div>
  );
}

function CopyButton({ text, label, variant = "secondary" }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API needs a secure context (https/localhost). Fall back to
      // a hidden textarea + execCommand so copy still works on plain http.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* give up silently */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className={`btn btn-${variant}`}
      onClick={copy}
      style={{ whiteSpace: "nowrap" }}
      title="Copy to clipboard"
    >
      <Icon name={copied ? "check" : "copy"} size={13} /> {copied ? "Copied" : (label || "Copy")}
    </button>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 4000,
    background: "rgba(15,23,42,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
  },
  card: {
    background: "var(--surface, #fff)", color: "var(--ink-900, #0f172a)",
    width: "100%", maxWidth: 440, borderRadius: 12,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)", padding: 20,
  },
  head: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 },
  iconWrap: {
    flex: "0 0 auto", width: 36, height: 36, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--primary-50, #eff6ff)", color: "var(--primary, #2563eb)",
  },
  title: { fontWeight: 700, fontSize: 15 },
  sub: { fontSize: 12, color: "var(--ink-500, #64748b)", marginTop: 2 },
  fieldRow: { display: "flex", gap: 8, alignItems: "center" },
  footer: { display: "flex", justifyContent: "space-between", gap: 8, marginTop: 18 },
};
